import { Link, useNavigate } from 'react-router-dom';
import type { AuctionCard as AuctionCardType } from '@/types/domain';
import { formatMoney, formatTimeLeftFull } from '@/lib/format';
import { useCountdown } from '@/hooks/useCountdown';
import { Tag } from '@/components/ui/Tag';
import { haptic } from '@/lib/telegram';
import clsx from 'clsx';
import styles from './AuctionCard.module.css';

interface Props {
  auction: AuctionCardType;
  index: number;
}

export const AuctionCard = ({ auction, index }: Props) => {
  const time = useCountdown(auction.ends_at);
  const isActive = auction.status === 'active';
  const isUrgent = isActive && time.totalMs < 60 * 60 * 1000 && !time.isOver; // <1 час

  // Таймер идёт только у активных торгов. До одобрения модератором ends_at —
  // лишь предварительная оценка, поэтому показываем статус, а не обратный отсчёт.
  const timerLabel = !isActive
    ? cardStatusLabel(auction.status)
    : time.isOver
      ? 'Завершён'
      : time.days > 0
        ? `${time.days}д ${time.hours}ч`
        : formatTimeLeftFull(auction.ends_at).slice(3); // обрезаем "00:" дни

      const navigate = useNavigate();

      const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        haptic.light();
      
        const doc = document as Document & {
          startViewTransition?: (cb: () => void) => void;
        };
      
        if (doc.startViewTransition) {
          doc.startViewTransition(() => {
            navigate(`/auction/${auction.id}`);
          });
        } else {
          navigate(`/auction/${auction.id}`);
        }
      };
      
      return (
        <Link
          to={`/auction/${auction.id}`}
          className={styles.card}
          onClick={handleClick}
        >
      <div className={styles.coverWrap}>
        {auction.cover_photo ? (
          <img
            src={auction.cover_photo.url_medium}
            alt={auction.title}
            className={styles.cover}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className={styles.coverPlaceholder}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="9" cy="9" r="2" />
              <path d="m21 15-5-5L5 21" />
            </svg>
          </div>
        )}

        {/* Edition number — коллекционная подача */}
        <div className={styles.edition}>
          <span className={styles.editionHash}>#</span>
          {String(index + 1).padStart(3, '0')}
        </div>

        {/* Таймер */}
        <div className={clsx(styles.timer, isUrgent && styles.timerUrgent)}>
          {timerLabel}
        </div>

        {/* Buy now бейдж */}
        {auction.buy_now_price && (
          <div className={styles.buyNow}>ВЫКУП СРАЗУ</div>
        )}
      </div>

      <div className={styles.body}>
        <div className={styles.tagsRow}>
          {auction.tags.slice(0, 3).map((t) => (
            <Tag key={t.id} variant="subtle" className={styles.tagOnCard}>
              {t.name_ru}
            </Tag>
          ))}
        </div>

        <h3 className={styles.title}>{auction.title}</h3>

        <div className={styles.priceRow}>
          <div className={styles.priceBlock}>
            <span className={styles.priceLabel}>текущая ставка</span>
            <span className={styles.price}>{formatMoney(auction.current_price)}</span>
          </div>
          <div className={styles.bids}>
            <span className={styles.bidsNum}>{auction.bids_count}</span>
            <span className={styles.bidsLabel}>ставок</span>
          </div>
        </div>

        <div className={styles.seller}>
          {auction.seller.avatar_url && (
            <img
              src={auction.seller.avatar_url}
              alt=""
              className={styles.sellerAvatar}
            />
          )}
          <span className={styles.sellerName}>{auction.seller.display_name}</span>
          <span className={styles.sellerRating}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            {auction.seller.rating.toFixed(1)}
          </span>
        </div>
      </div>
    </Link>
  );
};
const cardStatusLabel = (s: string): string => {
  const map: Record<string, string> = {
    draft: 'Черновик',
    pending_review: 'На модерации',
    scheduled: 'Запланирован',
    finished: 'Завершён',
    sold: 'Продано',
    unsold: 'Не продано',
    cancelled: 'Отменён',
    rejected: 'Отклонён',
  };
  return map[s] ?? 'Завершён';
};